import type {
  CleaningCompletionReportResponse,
  CleaningCompletionReportTotals,
} from '@/shared/api';
import { formatNumber } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import { CompletionReportApartmentTable } from './CompletionReportApartmentTable';
import { CompletionReportChart } from './CompletionReportChart';
import { CompletionReportDetailPanel } from './CompletionReportDetailPanel';
import { describeQuality, evidenceLabel, guestsLabel } from './completionReportModel';

/** Summen, Diagramm je Apartment, Tabelle und Detail; ohne Reinigung im Zeitraum ein Satz. */
export function CompletionReportResults({
  report,
  from,
  to,
  onSelectApartment,
}: {
  report: CleaningCompletionReportResponse;
  from: string;
  to: string;
  onSelectApartment: (propertyId: string) => void;
}) {
  if (report.totals.cleaningCount === 0)
    return (
      <section className="border-hairline rounded-tile flex flex-col gap-1 border bg-white p-5">
        <b className="text-[15px]">Keine abgeschlossenen Reinigungen</b>
        <p className="text-ink-soft text-sm">
          Im gewählten Zeitraum und Filter ist nichts erledigt.
        </p>
      </section>
    );
  return (
    <div className="flex flex-col gap-4">
      <TotalsBand totals={report.totals} />
      <CompletionReportChart apartments={report.apartments} onSelectApartment={onSelectApartment} />
      <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-4 xl:grid-cols-[minmax(0,1fr)_25rem]">
        <CompletionReportApartmentTable
          report={report}
          from={from}
          to={to}
          onSelectApartment={onSelectApartment}
        />
        <CompletionReportDetailPanel apartment={report.selectedApartment} />
      </div>
    </div>
  );
}

function TotalsBand({ totals }: { totals: CleaningCompletionReportTotals }) {
  const values = [
    {
      label: 'Abgeschlossene Reinigungen',
      value: formatNumber(totals.cleaningCount),
      detail: 'im gewählten Zeitraum',
      alert: false,
    },
    {
      label: 'Erfasste Gäste',
      value: guestsLabel(totals),
      detail: describeQuality(totals).join(' · ') || 'Keine Gäste-Stände',
      alert: false,
    },
    {
      label: 'Fotonachweise',
      value: evidenceLabel(totals),
      detail:
        totals.openIssueCount === 0
          ? 'Keine offenen Meldungen'
          : `${formatNumber(totals.openIssueCount)} offene Meldungen`,
      alert: totals.openIssueCount > 0,
    },
  ];
  return (
    <section
      aria-label="Summen"
      className="border-hairline rounded-tile grid overflow-hidden border bg-white md:grid-cols-3"
    >
      {values.map(({ label, value, detail, alert }, index) => (
        <div
          key={label}
          className={cn(
            'flex flex-col gap-1 p-4',
            index > 0 && 'border-hairline border-t md:border-l md:border-t-0',
          )}
        >
          <span className="text-ink-soft font-mono text-[11px] font-semibold uppercase tracking-[0.14em]">
            {label}
          </span>
          <span className="font-serif text-2xl font-bold">{value}</span>
          <span className={cn('text-xs', alert ? 'text-sealwax font-semibold' : 'text-ink-soft')}>
            {detail}
          </span>
        </div>
      ))}
    </section>
  );
}
